// Modelo de energía REACTIVA y penalización CREG — PORTADO de FluxAI (modelo CREG 015/2018
// Cap.12 · 199/2019 · 195/2020 · 101 035/2024) al stack de Solar Hub. JS puro y testeable.
// Es un MOTOR compartido de la suite (contrato solarhub.engine.v1): la versión con dato real
// vive en FluxAI (telemetría); acá queda el modelo para estimar/explicar en el estudio.
//
// Reglas:
//  · Inductiva penalizable: la que excede el 50% de la energía ACTIVA del período.
//  · Capacitiva penalizable: la que excede un margen exento (101 035/2024).
//  · Factor M (CREG 015/2018 Cap.12): M ∈ [1,12]. Un mes es "condición" si tiene >10 días con
//    exceso. M=1 los primeros 12 meses-condición; del mes-condición 13 en adelante, +1 por mes
//    hasta 12. Se reinicia tras 3 meses LIMPIOS (sin condición) consecutivos.
//  · Cobro estimado = exceso penalizable (kVArh) × cargo de distribución (COP/kVArh) × M.

/** ¿El mes cumple la CONDICIÓN de penalización? (>10 días con exceso de reactiva). */
export function esMesCondicion(diasConExceso) {
  return (Number(diasConExceso) || 0) > 10;
}

/**
 * Factor M a partir de los meses-condición ACUMULADOS (consecutivos, ya reiniciados por
 * la regla de 3 meses limpios). 1..12.
 */
export function factorM(mesesCondicionAcumulados) {
  const m = Number(mesesCondicionAcumulados) || 0;
  if (m <= 12) return 1;            // primeros 12 meses-condición
  return Math.min(12, m - 11);      // mes 13 → 2, … mes 23 → 12 (tope)
}

/**
 * Recorre un historial mensual y calcula el Factor M del ÚLTIMO mes, aplicando el reinicio
 * por 3 meses limpios consecutivos. `historial` = [{ diasConExceso }] del más viejo al más nuevo.
 * Devuelve { factorM, mesesCondicion } al final del historial.
 */
export function factorMDeHistorial(historial = [], mesesLimpiosParaReiniciar = 3) {
  let mesesCondicion = 0;
  let limpiosSeguidos = 0;
  for (const mes of historial) {
    if (esMesCondicion(mes?.diasConExceso)) {
      mesesCondicion += 1;
      limpiosSeguidos = 0;
    } else {
      limpiosSeguidos += 1;
      if (limpiosSeguidos >= mesesLimpiosParaReiniciar) mesesCondicion = 0;
    }
  }
  return { factorM: factorM(mesesCondicion), mesesCondicion };
}

/** Inductiva penalizable (kVArh): la que excede el 50% de la energía activa (kWh). */
export function inductivaPenalizable(activaKwh, inductivaKvarh) {
  return Math.max(0, (Number(inductivaKvarh) || 0) - 0.5 * (Number(activaKwh) || 0));
}

/** Capacitiva penalizable (kVArh): la que excede el margen exento (kVArh). */
export function capacitivaPenalizable(capacitivaKvarh, margenExentoKvarh = 0) {
  return Math.max(0, (Number(capacitivaKvarh) || 0) - (Number(margenExentoKvarh) || 0));
}

/**
 * Cobro estimado de reactiva del período.
 * @param excesoKvarh  inductiva penalizable + capacitiva penalizable
 * @param cargoDistribucionCopKvarh  cargo D del pliego (COP/kVArh)
 * @param M  Factor M del período (1..12)
 */
export function cobroReactiva(excesoKvarh, cargoDistribucionCopKvarh, M = 1) {
  return Math.max(0, (Number(excesoKvarh) || 0) * (Number(cargoDistribucionCopKvarh) || 0) * (Number(M) || 1));
}

/**
 * Evaluación completa del período. Devuelve excesos, Factor M y cobro estimado.
 * @param p { activaKwh, inductivaKvarh, capacitivaKvarh, margenCapacitivaKvarh,
 *            cargoDistribucionCopKvarh, historial[] | mesesCondicion }
 */
/**
 * Energía reactiva inductiva (kVArh) derivada del factor de potencia declarado.
 *
 *   kVArh = kWh × tan(arccos(FP))
 *
 * Es la relación trigonométrica del triángulo de potencias, aplicada a la energía del
 * período. Se usa cuando el cliente conoce su FP (viene en la factura de un comercial o
 * industrial) pero no el detalle de kVArh. **No se estima un FP cuando no lo declara**:
 * sin dato no se muestra penalización, porque inventarlo produciría un cobro que no existe.
 *
 * FP fuera de (0,1] devuelve null — no hay lectura física que interpretar.
 */
export function kvarhDesdeFP(activaKwh, factorPotencia) {
  const kwh = Number(activaKwh);
  const fp = Number(factorPotencia);
  if (!(kwh > 0) || !(fp > 0) || fp > 1) return null;
  return kwh * Math.tan(Math.acos(fp));
}

export function evaluarReactiva(p = {}) {
  const ind = inductivaPenalizable(p.activaKwh, p.inductivaKvarh);
  const cap = capacitivaPenalizable(p.capacitivaKvarh, p.margenCapacitivaKvarh);
  const exceso = ind + cap;
  const M = Array.isArray(p.historial)
    ? factorMDeHistorial(p.historial).factorM
    : factorM(p.mesesCondicion);
  const cobro = cobroReactiva(exceso, p.cargoDistribucionCopKvarh, M);
  return {
    inductivaPenalizableKvarh: round(ind, 2),
    capacitivaPenalizableKvarh: round(cap, 2),
    excesoKvarh: round(exceso, 2),
    factorM: M,
    cobroEstimadoCop: Math.round(cobro),
  };
}

function round(x, d) { const p = Math.pow(10, d); return Math.round((x + Number.EPSILON) * p) / p; }
